import React, { useState, useEffect } from 'react';
import {
  Sport,
  Location,
  GameProposal,
  PadelCategory,
  BeachTennisCategory,
  Player,
  GameGender,
  TennisCategory,
} from '../types';
import { generateTimeOptions } from '../utils/timeUtils';
import { toLocalISOString, normalizeDate } from '../utils/dateUtils';
import { validateTimes, formatTimeForSelect } from '../utils/formValidation';
import CategorySelector from './game/CategorySelector';
import LocationSelector from './game/LocationSelector';
import LocationInfoTooltip from './tooltips/LocationInfoTooltip';
import CategoryTooltip from './tooltips/CategoryTooltip';
import { X, Search } from 'lucide-react';

const padelCategories: PadelCategory[] = ['CAT 1', 'CAT 2', 'CAT 3', 'CAT 4', 'CAT 5', 'CAT 6'];
const beachTennisCategories: BeachTennisCategory[] = [
  'BEGINNER',
  'CAT C',
  'CAT B',
  'CAT A',
  'PROFESSIONAL'
];
const tennisCategories: TennisCategory[] = [
  '1.0', '1.5', '2.0', '2.5', '3.0', '3.5', '4.0', '4.5', '5.0', '5.5', '6.0', '6.5', '7.0'
];

interface GameProposalFormProps {
  onSubmit: (data: {
    sport: Sport;
    locations: string[];
    date: string;
    startTime: string;
    endTime: string;
    description: string;
    requiredCategories: (PadelCategory | BeachTennisCategory | TennisCategory)[];
    gender: GameGender;
    maxPlayers: number;
    is_public: boolean;
    groups?: string[];
  }) => void;
  onClose: () => void;
  initialData?: Partial<GameProposal>;
  isEditing?: boolean;
  currentUser: Player | null;
  availableLocations: Location[];
}

export default function GameProposalForm({
  onSubmit,
  onClose,
  initialData,
  isEditing = false,
  currentUser,
  availableLocations
}: GameProposalFormProps) {
  const [formData, setFormData] = useState({
    sport: initialData?.sport || 'padel' as Sport,
    locations: initialData?.locations || [] as string[],
    date: initialData?.date ? toLocalISOString(new Date(initialData.date)) : '',
    startTime: formatTimeForSelect(initialData?.start_time || initialData?.startTime || ''),
    endTime: formatTimeForSelect(initialData?.end_time || initialData?.endTime || ''),
    description: initialData?.description || '',
    requiredCategories: initialData?.requiredCategories || [] as (PadelCategory | BeachTennisCategory | TennisCategory)[],
    gender: initialData?.gender || 'male' as GameGender,
    maxPlayers: initialData?.maxPlayers || 4,
  });

  const [error, setError] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  const validateForm = () => {
    if (formData.locations.length === 0) {
      setError('Select at least one location');
      return false;
    }
    if (formData.requiredCategories.length === 0) {
      setError('Select at least one category');
      return false;
    }
    if (!validateTimes(formData.startTime, formData.endTime)) {
      setError('End time must be after start time');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    try {
      const normalizedDate = normalizeDate(formData.date);

      // Mock game creation - just call onSubmit with the form data
      onSubmit({
        sport: formData.sport,
        locations: formData.locations,
        date: normalizedDate,
        startTime: formData.startTime,
        endTime: formData.endTime,
        description: formData.description,
        requiredCategories: formData.requiredCategories,
        gender: formData.gender,
        maxPlayers: formData.maxPlayers,
        is_public: isPublic,
        groups: selectedGroups
      });

      // Close the form on success
      onClose();

    } catch (error) {
      console.error('Error in form submission:', error);
      setError('Error creating game. Please try again.');
    }
  };

  useEffect(() => {
    if (!isPublic && selectedGroups.length === 0) {
      setIsPublic(true);
    }
  }, [selectedGroups, isPublic]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sport
        </label>
        <div className="flex flex-wrap gap-2">
          {(['padel', 'beach-tennis', 'tennis'] as Sport[]).map((sportOption) => (
            <button
              key={sportOption}
              type="button"
              onClick={() => setFormData({ ...formData, sport: sportOption })}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.sport === sportOption
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {sportOption === 'padel' ? 'Padel' : sportOption === 'beach-tennis' ? 'Beach Tennis' : 'Tennis'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          This game will be visible for:
        </label>
        <div className="flex gap-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              checked={isPublic}
              onChange={() => setIsPublic(true)}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="ml-2 text-gray-700">Everyone</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              checked={!isPublic}
              onChange={() => setIsPublic(false)}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="ml-2 text-gray-700">Selected Groups Only</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Game Type
        </label>
        <div className="flex flex-wrap gap-2">
          {(['mixed', 'male', 'female'] as GameGender[]).map((genderOption) => (
            <button
              key={genderOption}
              type="button"
              onClick={() => setFormData({ ...formData, gender: genderOption })}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.gender === genderOption
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {genderOption === 'mixed' ? 'Mixed' : genderOption === 'male' ? 'Male' : 'Female'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Max Players
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, maxPlayers: 4 })}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.maxPlayers === 4
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            4 Players
          </button>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, maxPlayers: 8 })}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.maxPlayers === 8
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            8 Players
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Locations
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {availableLocations.map((location) => (
            <button
              key={location.id}
              type="button"
              onClick={() => {
                const newLocations = formData.locations.includes(location.id)
                  ? formData.locations.filter(id => id !== location.id)
                  : [...formData.locations, location.id];
                setFormData({ ...formData, locations: newLocations });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${formData.locations.includes(location.id)
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {location.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Required Categories
        </label>
        <div className="flex flex-wrap gap-2">
          {formData.sport === 'padel' && padelCategories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => {
                const newCategories = formData.requiredCategories.includes(category)
                  ? formData.requiredCategories.filter(c => c !== category)
                  : [...formData.requiredCategories, category];
                setFormData({ ...formData, requiredCategories: newCategories });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.requiredCategories.includes(category)
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {category}
            </button>
          ))}
          {formData.sport === 'beach-tennis' && beachTennisCategories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => {
                const newCategories = formData.requiredCategories.includes(category)
                  ? formData.requiredCategories.filter(c => c !== category)
                  : [...formData.requiredCategories, category];
                setFormData({ ...formData, requiredCategories: newCategories });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.requiredCategories.includes(category)
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {category}
            </button>
          ))}
          {formData.sport === 'tennis' && tennisCategories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => {
                const newCategories = formData.requiredCategories.includes(category)
                  ? formData.requiredCategories.filter(c => c !== category)
                  : [...formData.requiredCategories, category];
                setFormData({ ...formData, requiredCategories: newCategories });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.requiredCategories.includes(category)
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date and Time
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <input
                type="date"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min={toLocalISOString(new Date())}
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>
            <div className="relative">
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.startTime}
                onChange={(e) => {
                  const newTime = e.target.value;
                  if (validateTimes(newTime, formData.endTime)) {
                    setFormData({ ...formData, startTime: newTime });
                  }
                }}
              >
                <option value="">Start Time</option>
                {generateTimeOptions().map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative">
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.endTime}
                onChange={(e) => {
                  const newTime = e.target.value;
                  if (validateTimes(formData.startTime, newTime)) {
                    setFormData({ ...formData, endTime: newTime });
                  }
                }}
              >
                <option value="">End Time</option>
                {generateTimeOptions().map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={4}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="(OPTIONAL) Add important information about the game..."
        />
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-600 hover:text-gray-900"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          {isEditing ? 'Save Changes' : 'Propose Game'}
        </button>
      </div>
    </form>
  );
}